/**
 * Share filter preset with other users
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import { Button, Card, Checkbox } from '@/components/ui';
import { useShareableUsers } from '@/features/smswiser-campaigns/hooks/useSavedFilters';
import { cn } from '@/lib/utils';

interface ShareFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShare: (userIds: string[]) => Promise<void>;
  onUnshare: (userId: string) => Promise<void>;
  submitting: boolean;
  currentSharedUserIds: string[];
}

export const ShareFilterModal: FC<ShareFilterModalProps> = ({
  open, onOpenChange, onShare, onUnshare, submitting, currentSharedUserIds,
}) => {
  const { data: users = [], isLoading } = useShareableUsers();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const toggleUser = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleShare = async () => {
    const newIds = [...selected].filter((id) => !currentSharedUserIds.includes(id));
    if (newIds.length === 0) return;
    await onShare(newIds);
    setSelected(new Set());
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[200]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} aria-hidden="true" />
        <motion.div initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.15 }}
          className="absolute inset-0 flex items-center justify-center p-6" role="dialog" aria-modal="true">
          <Card className="w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5">
              <h2 className="text-[13px] font-semibold tracking-tight">Share filter</h2>
              <button onClick={() => onOpenChange(false)} className="rounded-md p-1 text-muted-foreground/60 hover:text-foreground" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[40vh] overflow-y-auto border-t px-5 py-3">
              {isLoading && <p className="text-xs text-muted-foreground">Loading users…</p>}
              {!isLoading && users.length === 0 && <p className="text-xs text-muted-foreground">No other users.</p>}
              <div className="space-y-0.5">
                {users.map((user) => {
                  const isShared = currentSharedUserIds.includes(user.id);
                  return (
                    <UserRow key={user.id} user={user} isShared={isShared}
                      isSelected={selected.has(user.id)} onToggle={() => toggleUser(user.id)}
                      onUnshare={() => onUnshare(user.id)} submitting={submitting} />
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button size="sm" onClick={handleShare} disabled={submitting || selected.size === 0}>
                {submitting ? 'Sharing…' : `Share (${selected.size})`}
              </Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

interface UserRowProps {
  user: { id: string; firstName: string; lastName: string; email: string };
  isShared: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onUnshare: () => void;
  submitting: boolean;
}

const UserRow: FC<UserRowProps> = ({ user, isShared, isSelected, onToggle, onUnshare, submitting }) => (
  <div className={cn('flex items-center justify-between rounded-md px-2 py-1.5', isShared && 'bg-muted/40')}>
    <label className="flex min-w-0 cursor-pointer items-center gap-2">
      {!isShared && <Checkbox checked={isSelected} onCheckedChange={onToggle} className="h-3.5 w-3.5" />}
      <div className="min-w-0">
        <p className="truncate text-xs font-medium">{user.firstName} {user.lastName}</p>
        <p className="truncate text-[10px] text-muted-foreground">{user.email}</p>
      </div>
    </label>
    {isShared && (
      <button onClick={onUnshare} disabled={submitting} aria-label={`Revoke access for ${user.firstName}`}
        className="shrink-0 rounded-md p-1 text-muted-foreground/60 hover:text-destructive">
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);
